/**
 * Created by katarinababic on 21.11.24.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { observer } from 'mobx-react';
import { Media, getMediaLargeOrMediumOrSmallest } from '../../Model/Media/Media';
import { useLanguage } from '../../Hooks/useLanguage';
import { MediaAnalyticsConfig, useMediaAnalytics } from '../../Hooks/useMediaAnalytics';
import { usePlausible } from '../../Contexts/PlausibleContext';
import styled from '@emotion/styled';
import {
  MediaController,
  MediaControlBar,
  MediaTimeRange,
  MediaTimeDisplay,
  MediaVolumeRange,
  MediaPlayButton,
  MediaMuteButton,
  MediaFullscreenButton,
  MediaSeekBackwardButton,
  MediaSeekForwardButton,
} from 'media-chrome/react';
import { Theme } from '../../Theme/Theme';
import { notUndefined } from '../../Utils/notUndefined';
import { MeditationBackground } from './MeditationBackground';

const PlayerContainer = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  overflow: hidden;
  border-radius: 16px;

  @media (max-width: 768px) {
    border-radius: 12px;
  }
`;

const TopChromeContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: flex-start;
  width: 100%;
  padding: 12px;
  box-sizing: border-box;
  z-index: 10;
  opacity: 1 !important;
  visibility: visible !important;
`;

const LanguageSelectorContainer = styled.div`
  display: flex;
  gap: 6px;
`;

const FlagButton = styled.button<{ $isSelected: boolean }>`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  border: 2px solid ${({ $isSelected }) => ($isSelected ? Theme.Colors.primary : 'rgba(255, 255, 255, 0.3)')};
  background: rgba(0, 0, 0, 0.5);
  cursor: pointer;
  overflow: hidden;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  transition: all 0.2s ease;
  box-shadow: ${({ $isSelected }) => ($isSelected ? `0 0 0 2px ${Theme.Colors.primary}40` : 'none')};

  &:hover {
    border-color: ${Theme.Colors.primary};
    transform: scale(1.1);
  }
`;

const languageToFlag: Record<string, string> = {
  en: '🇬🇧',
  de: '🇩🇪',
  es: '🇪🇸',
  fr: '🇫🇷',
  it: '🇮🇹',
  pt: '🇵🇹',
  nl: '🇳🇱',
  sv: '🇸🇪',
  da: '🇩🇰',
  fi: '🇫🇮',
  no: '🇳🇴',
  pl: '🇵🇱',
  cs: '🇨🇿',
  ro: '🇷🇴',
  hu: '🇭🇺',
  el: '🇬🇷',
  bg: '🇧🇬',
  hr: '🇭🇷',
  lt: '🇱🇹',
  lv: '🇱🇻',
  et: '🇪🇪',
  sk: '🇸🇰',
  sl: '🇸🇮',
};

const languageToName: Record<string, string> = {
  en: 'English',
  de: 'Deutsch',
  es: 'Español',
  fr: 'Français',
  it: 'Italiano',
  pt: 'Português',
  nl: 'Nederlands',
  sv: 'Svenska',
  da: 'Dansk',
  fi: 'Suomi',
  no: 'Norsk',
  pl: 'Polski',
  cs: 'Čeština',
  ro: 'Română',
  hu: 'Magyar',
  el: 'Ελληνικά',
  bg: 'Български',
  hr: 'Hrvatski',
  lt: 'Lietuvių',
  lv: 'Latviešu',
  et: 'Eesti',
  sk: 'Slovenčina',
  sl: 'Slovenščina',
};

const StyledMediaControlBar = styled(MediaControlBar)`
  --media-control-background: transparent;
  --media-control-hover-background: transparent;
  --media-primary-color: rgba(255, 255, 255, 0.95);
  --media-secondary-color: ${Theme.Colors.primary};
  --media-range-track-background: rgba(255, 255, 255, 0.15);
  --media-range-track-border-radius: 100px;
  --media-range-track-height: 4px;
  --media-range-thumb-background: white;
  --media-range-thumb-width: 12px;
  --media-range-thumb-height: 12px;
  --media-range-thumb-border-radius: 50%;
  --media-range-bar-color: ${Theme.Colors.primary};
  --media-time-range-buffered-color: rgba(255, 255, 255, 0.3);
  --media-control-padding: 6px;
  --media-font-family: inherit;

  background: linear-gradient(to top, rgba(0, 0, 0, 0.7) 0%, rgba(0, 0, 0, 0.4) 60%, transparent 100%);
  padding: 24px 16px 14px;
  border-radius: 0 0 16px 16px;
  backdrop-filter: blur(4px);
  gap: 4px;

  media-controller[mediaisfullscreen] & {
    border-radius: 0;
  }

  media-play-button,
  media-seek-backward-button,
  media-seek-forward-button,
  media-mute-button,
  media-fullscreen-button {
    --media-button-icon-width: 20px;
    --media-button-icon-height: 20px;
    border-radius: 6px;
    padding: 6px;
    transition:
      background 0.15s ease,
      transform 0.15s ease;

    &:hover {
      background: rgba(255, 255, 255, 0.15);
      transform: scale(1.05);
    }

    &:active {
      transform: scale(0.95);
    }
  }

  media-play-button {
    --media-button-icon-width: 24px;
    --media-button-icon-height: 24px;
  }

  media-time-display {
    font-size: 12px;
    font-weight: 500;
    font-variant-numeric: tabular-nums;
    letter-spacing: 0.02em;
    opacity: 0.85;
    padding: 0 4px;
    pointer-events: none;
  }

  media-time-range {
    --media-range-track-height: 4px;
    --media-range-thumb-opacity: 0;
    flex-grow: 1;
    margin: 0 8px;

    &:hover {
      --media-range-track-height: 6px;
      --media-range-thumb-opacity: 1;
    }
  }

  media-volume-range {
    --media-range-track-height: 3px;
    --media-range-thumb-width: 10px;
    --media-range-thumb-height: 10px;
    width: 70px;
    opacity: 0.9;

    @media (max-width: 600px) {
      width: 50px;
    }
  }
`;

const StyledAudio = styled.audio`
  display: none;
`;

export type KinasticAudioPlayerProps = {
  audioTracks: Media[];
  title?: string;
  /** Content entry id, attached to Plausible Page View / Watch Progress / Pause / Complete events when provided. */
  entryId?: string;
  backgroundImage?: string;
  onWatchDuration?: (watchedDurationInSeconds: number, totalDurationInSeconds: number) => void;
  analyticsConfig?: MediaAnalyticsConfig;
  onPlayStateChange?: (isPlaying: boolean) => void;
};

export const KinasticAudioPlayer: React.FC<KinasticAudioPlayerProps> = observer(
  ({ audioTracks, title, entryId, backgroundImage, onWatchDuration, analyticsConfig, onPlayStateChange }) => {
    const { language } = useLanguage();
    const audioRef = useRef<HTMLAudioElement>(null);
    const mediaAnalytics = useMediaAnalytics(analyticsConfig);
    const { trackPlausible } = usePlausible();
    const playStartTimeRef = useRef<number | null>(null);
    const totalListenedTimeRef = useRef<number>(0);
    const reportedDurationRef = useRef(false);
    const currentTimeRef = useRef<number>(0);
    const wasPlayingRef = useRef<boolean>(false);
    const sentMilestonesRef = useRef<Set<number>>(new Set());
    const contentCompleteRef = useRef(false);

    const [selectedLanguage, setSelectedLanguage] = useState(language);
    const [totalDuration, setTotalDuration] = useState(0);
    const [hasAudioEnded, setHasAudioEnded] = useState(false);
    const [isMobile, setIsMobile] = useState(false);

    const audioMedia =
      audioTracks.find((a) => a.language === selectedLanguage) ??
      audioTracks.find((a) => a.language === 'en') ??
      audioTracks[0];

    const availableLanguages = useMemo(
      () =>
        audioTracks
          .map((a) => a.language)
          .filter((v) => !!v)
          .filter(notUndefined),
      [audioTracks],
    );

    const audioUrl = audioMedia ? getMediaLargeOrMediumOrSmallest(audioMedia) : undefined;

    React.useEffect(() => {
      trackPlausible('Page View', {
        contentType: 'audio',
        ...(title ? { pageTitle: title } : {}),
        ...(entryId ? { contentId: entryId } : {}),
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [title, entryId]);
    
    // Check for mobile
    useEffect(() => {
      const checkMobile = () => {
        setIsMobile(window.matchMedia('(max-width: 768px)').matches);
      };
      checkMobile();
      const mediaQuery = window.matchMedia('(max-width: 768px)');
      mediaQuery.addEventListener('change', checkMobile);
      return () => mediaQuery.removeEventListener('change', checkMobile);
    }, []);

    const handleChangeLanguage = useCallback((lang: string) => {
      if (audioRef.current) {
        currentTimeRef.current = audioRef.current.currentTime;
        wasPlayingRef.current = !audioRef.current.paused;
      }
      mediaAnalytics.onLanguageChange(selectedLanguage, lang);
      setSelectedLanguage(lang);
    }, [selectedLanguage, mediaAnalytics]);

    const handlePlay = useCallback(() => {
      playStartTimeRef.current = Date.now();
      setHasAudioEnded(false);
      reportedDurationRef.current = false;
      mediaAnalytics.onPlay();
      onPlayStateChange?.(true);
      trackPlausible('Start Watching', {
        contentType: 'audio',
        ...(title ? { pageTitle: title } : {}),
        ...(entryId ? { contentId: entryId } : {}),
      });
    }, [mediaAnalytics, onPlayStateChange, trackPlausible, title, entryId]);

    const handlePause = useCallback(() => {
      if (playStartTimeRef.current) {
        const duration = Date.now() - playStartTimeRef.current;
        totalListenedTimeRef.current += duration;
        playStartTimeRef.current = null;
      }
      mediaAnalytics.onPause();
      onPlayStateChange?.(false);
      const audio = audioRef.current;
      if (audio && !audio.ended) {
        trackPlausible('Pause Watching', {
          seconds: `${Math.floor(audio.currentTime)}s`,
          duration: `${Math.floor(totalDuration)}s`,
          contentType: 'audio',
          ...(title ? { pageTitle: title } : {}),
          ...(entryId ? { contentId: entryId } : {}),
        });
      }
    }, [mediaAnalytics, onPlayStateChange, trackPlausible, totalDuration, title, entryId]);

    const handleEnded = useCallback(() => {
      if (playStartTimeRef.current) {
        const duration = Date.now() - playStartTimeRef.current;
        totalListenedTimeRef.current += duration;
        playStartTimeRef.current = null;
      }
      const watchedSeconds = Math.floor(totalListenedTimeRef.current / 1000);
      onWatchDuration?.(watchedSeconds, totalDuration);
      setHasAudioEnded(true);
      reportedDurationRef.current = true;
      mediaAnalytics.onEnded();
      onPlayStateChange?.(false);
      if (!contentCompleteRef.current) {
        contentCompleteRef.current = true;
        trackPlausible('Content Complete', {
          seconds: `${watchedSeconds}s`,
          contentType: 'audio',
          ...(title ? { pageTitle: title } : {}),
          ...(entryId ? { contentId: entryId } : {}),
        });
      }
    }, [onWatchDuration, totalDuration, mediaAnalytics, onPlayStateChange, trackPlausible, title, entryId]);

    const handleDurationChange = useCallback(() => {
      const audio = audioRef.current;
      if (audio && Number.isFinite(audio.duration)) {
        setTotalDuration(audio.duration);
      }
    }, []);

    const handleLoadedMetadata = useCallback(() => {
      const audio = audioRef.current;
      if (audio && currentTimeRef.current > 0) {
        audio.currentTime = currentTimeRef.current;
        if (wasPlayingRef.current) {
          audio.play().catch(console.error);
        }
      }
    }, []);

    const handleTimeUpdate = useCallback(() => {
      const audio = audioRef.current;
      if (audio && audio.currentTime > 0) {
        currentTimeRef.current = audio.currentTime;
        mediaAnalytics.onTimeUpdate(audio.currentTime, audio.duration || 0);
        const duration = audio.duration;
        if (duration > 0) {
          const pct = (audio.currentTime / duration) * 100;
          const completeMilestone = 98;
          for (const milestone of [5, 10, 20, 40, 80, completeMilestone]) {
            if (pct >= milestone && !sentMilestonesRef.current.has(milestone)) {
              sentMilestonesRef.current.add(milestone);
              trackPlausible('Watch Progress', {
                milestone: `${milestone}%`,
                seconds: `${Math.floor(audio.currentTime)}s`,
                duration: `${Math.floor(duration)}s`,
                contentType: 'audio',
                ...(title ? { pageTitle: title } : {}),
                ...(entryId ? { contentId: entryId } : {}),
              });
              if (milestone === completeMilestone && !contentCompleteRef.current) {
                contentCompleteRef.current = true;
                trackPlausible('Content Complete', {
                  seconds: `${Math.floor(audio.currentTime)}s`,
                  contentType: 'audio',
                  ...(title ? { pageTitle: title } : {}),
                  ...(entryId ? { contentId: entryId } : {}),
                });
              }
            }
          }
        }
      }
    }, [mediaAnalytics, trackPlausible, title, entryId]);

    useEffect(() => {
      return () => {
        if (reportedDurationRef.current) return;

        if (playStartTimeRef.current) {
          const duration = Date.now() - playStartTimeRef.current;
          totalListenedTimeRef.current += duration;
        }

        if (!hasAudioEnded) {
          const watchedSeconds = Math.floor(totalListenedTimeRef.current / 1000);
          if (watchedSeconds > 0) {
            onWatchDuration?.(watchedSeconds, totalDuration);
          }
        }
      };
    }, [onWatchDuration, totalDuration, hasAudioEnded]);

    // Reset state when audio changes
    useEffect(() => {
      setHasAudioEnded(false);
      totalListenedTimeRef.current = 0;
      playStartTimeRef.current = null;
      reportedDurationRef.current = false;
      sentMilestonesRef.current = new Set();
      contentCompleteRef.current = false;
    }, [audioMedia]);

    if (!audioUrl) {
      return null;
    }

    return (
      <PlayerContainer>
        <MediaController
          style={{
            width: '100%',
            height: '100%',
            backgroundColor: '#1a1a2e',
          }}
          autohide="-1"
        >
          <MeditationBackground
            key={`bg-${selectedLanguage}`}
            backgroundImage={backgroundImage}
            seed={selectedLanguage}
          />
          <StyledAudio
            ref={audioRef}
            slot="media"
            src={audioUrl}
            onPlay={handlePlay}
            onPause={handlePause}
            onEnded={handleEnded}
            onDurationChange={handleDurationChange}
            onLoadedMetadata={handleLoadedMetadata}
            onTimeUpdate={handleTimeUpdate}
            onSeeking={mediaAnalytics.onSeeking}
            onSeeked={mediaAnalytics.onSeeked}
            onWaiting={mediaAnalytics.onWaiting}
            onPlaying={mediaAnalytics.onPlaying}
          />
          {availableLanguages.length > 1 && (
            <TopChromeContainer slot="top-chrome">
              <LanguageSelectorContainer>
                {availableLanguages.map((lang) => (
                  <FlagButton
                    key={lang}
                    $isSelected={lang === selectedLanguage}
                    onClick={() => handleChangeLanguage(lang)}
                    title={languageToName[lang] || lang}
                  >
                    {languageToFlag[lang] || lang.toUpperCase().slice(0, 2)}
                  </FlagButton>
                ))}
              </LanguageSelectorContainer>
            </TopChromeContainer>
          )}
          <StyledMediaControlBar>
            <MediaPlayButton />
            {!isMobile && (
              <>
                <MediaSeekBackwardButton seekOffset={10} />
                <MediaSeekForwardButton seekOffset={20} />
              </>
            )}
            <MediaTimeRange />
            <MediaTimeDisplay showDuration />
            <MediaMuteButton />
            <MediaVolumeRange />
            <MediaFullscreenButton />
          </StyledMediaControlBar>
        </MediaController>
      </PlayerContainer>
    );
  },
);
